import React, { useEffect, useRef, useState } from 'react';
import { useBackgroundProcesses } from '../../hooks/useBackgroundProcesses';

const STATUS_LABELS = {
  queued: 'Pendiente',
  processing: 'En proceso',
  completed: 'Completado',
  partial: 'Completado con advertencias',
  failed: 'Error',
};

const STATUS_COLORS = {
  queued: 'bg-amber-500',
  processing: 'bg-blue-500',
  completed: 'bg-teal-600',
  partial: 'bg-amber-500',
  failed: 'bg-red-500',
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const runTitle = (run) => {
  if (run.process_kind === 'export') {
    if (run.guide_code) {
      return `Datos guía ${run.guide_code}`;
    }
    return run.type_label || 'Preparación de datos';
  }
  if (run.trigger === 'bulk_inconsistencies') {
    return 'Recálculo masivo de kardex';
  }
  if (run.guide_code) {
    return `Kardex — guía ${run.guide_code}`;
  }
  return run.trigger_label || 'Recálculo de kardex';
};

const runSubtitle = (run) => {
  if (run.process_kind === 'export') {
    return run.type_label || 'Exportación de datos para PDF';
  }
  if (run.trigger === 'bulk_inconsistencies') {
    return `${run.pairs_total ?? 0} par(es) producto-almacén`;
  }
  return run.trigger_label || 'Proceso en segundo plano';
};

const runDetail = (run) => {
  if (run.process_kind === 'export') {
    if (!run.is_active && run.status === 'completed') {
      return `Ítems listos: ${run.items_total ?? 0}`;
    }
    if (!run.is_active && run.status === 'failed') {
      return run.error_message || 'La preparación de datos falló.';
    }
    if (run.is_active) {
      return run.status === 'queued'
        ? 'En cola, esperando al worker…'
        : `Preparando ${run.items_processed ?? 0}/${run.items_total ?? 0} ítems…`;
    }
    return 'Preparación de datos para PDF o impresión';
  }

  if (!run.is_active && run.status === 'completed') {
    return `Procesados: ${run.processed_pairs ?? 0} · Movimientos: ${run.updated_movements ?? 0}`;
  }
  if (!run.is_active && run.status === 'partial') {
    return `Procesados: ${run.processed_pairs ?? 0} · Errores: ${run.failed_pairs ?? 0}`;
  }
  if (!run.is_active && run.status === 'failed') {
    return run.error_message || 'El proceso finalizó con error.';
  }
  if (run.is_active) {
    return run.status === 'queued'
      ? 'En cola, esperando al worker…'
      : `Procesando ${run.pairs_total ?? 0} par(es)…`;
  }
  return `Sin cambios: ${run.pairs_without_changes ?? 0}`;
};

const BackgroundProcessesButton = ({ compact = false }) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const {
    runs,
    activeCount,
    loading,
    error,
    refresh,
  } = useBackgroundProcesses({ enabled: open });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const badgeCount = activeCount;

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`relative inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50 hover:text-teal-700 transition-colors ${
          compact ? 'h-9 w-9' : 'h-10 w-10'
        }`}
        aria-label="Ver procesos en segundo plano"
        title="Procesos en segundo plano"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        {badgeCount > 0 ? (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-teal-600 text-white text-[10px] font-semibold flex items-center justify-center">
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 mt-2 w-[min(92vw,24rem)] rounded-xl border border-gray-200 bg-white shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Procesos en segundo plano</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {activeCount > 0
                    ? `${activeCount} proceso(s) activo(s)`
                    : 'Sin procesos activos'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => refresh()}
                className="text-xs text-teal-700 hover:text-teal-800 font-medium"
              >
                Actualizar
              </button>
            </div>
          </div>

          <div className="max-h-[24rem] overflow-y-auto">
            {loading && runs.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">Cargando procesos…</div>
            ) : error ? (
              <div className="px-4 py-6 text-center text-sm text-red-600">{error}</div>
            ) : runs.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No hay procesos recientes.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {runs.map((run) => (
                  <li key={`${run.process_kind || 'kardex'}-${run.id}`} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{runTitle(run)}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{runSubtitle(run)}</p>
                      </div>
                      <span className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full text-white ${STATUS_COLORS[run.status] || 'bg-gray-500'}`}>
                        {STATUS_LABELS[run.status] || run.status}
                      </span>
                    </div>

                    <div className="mt-2">
                      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            run.is_active ? 'bg-blue-500 animate-pulse' : (STATUS_COLORS[run.status] || 'bg-gray-400')
                          }`}
                          style={{ width: `${Math.max(0, Math.min(100, run.progress_percent ?? 0))}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-1.5">{runDetail(run)}</p>
                      <p className="text-[11px] text-gray-400 mt-1">
                        Inicio: {formatDateTime(run.started_at || run.created_at)}
                        {run.finished_at ? ` · Fin: ${formatDateTime(run.finished_at)}` : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BackgroundProcessesButton;
